import { inject } from '@angular/core';
import { tapResponse } from '@ngrx/operators';
import { patchState, signalStoreFeature, withMethods } from '@ngrx/signals';
import { addEntity, removeEntity, setAllEntities, updateEntity } from '@ngrx/signals/entities';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { map, pipe, switchMap } from 'rxjs';

import { Livrable } from '../models/livrable.model';
import { LivrableCreateRequest } from '../requests/livrable-create-request';
import { LivrableUpdateRequest } from '../requests/livrvable-update-request';
import { LivrableService } from '../services/livrable.service';
import { v7 as uuidv7 } from 'uuid';
import { ToastrErrorComponent } from '@shared/components/toastr/toastr-error.component';
import { toast } from 'ngx-sonner';
import { HttpErrorResponse } from '@angular/common/http';

export function avecMéthodesLivrables<_>() {
  return signalStoreFeature(
    // Updater
    withMethods((store) => ({
      chargerLivrables: (state: { livrables: Livrable[] }) => patchState(store, setAllEntities(state.livrables)),
      ajouterLivrable: (state: { livrable: Livrable }) => patchState(store, addEntity(state.livrable)),
      modifierLivrable: (state: { livrable: Livrable }) =>
        patchState(store, updateEntity({ id: state.livrable.id, changes: state.livrable })),
      supprimerLivrable: (id: string) => patchState(store, removeEntity(id)),
    })),

    // Effects
    withMethods((store, livrableService = inject(LivrableService)) => ({
      charger: rxMethod<string>(
        pipe(
          switchMap((idRéférentiel) =>
            livrableService.findAll(idRéférentiel).pipe(
              tapResponse({
                next: (result) => store.chargerLivrables({ livrables: result.items }),
                error: (err) => {
                  console.error(err);
                  const toastrId = uuidv7();
                  toast.custom(ToastrErrorComponent, {
                    componentProps: {
                      message: `Un problème est survenu lors de la récupération des livrables, contactez le support.`,
                      id: toastrId,
                    },
                    id: toastrId,
                  });
                },
              }),
            ),
          ),
        ),
      ),
      créer: rxMethod<{ data: LivrableCreateRequest; next: () => void; error: () => void }>(
        pipe(
          switchMap(({ data, next, error }) =>
            livrableService.créer(data).pipe(
              tapResponse({
                next: () => next(),
                error: (err: HttpErrorResponse) => {
                  console.error(err);
                  const toastrId = uuidv7();
                  toast.custom(ToastrErrorComponent, {
                    componentProps: {
                      message:
                        err.error.detail ??
                        `Un problème est survenu lors de la création des livrables, contactez le support.`,
                      id: toastrId,
                    },
                    id: toastrId,
                  });
                  error();
                },
              }),
            ),
          ),
        ),
      ),
      modifier: rxMethod<{
        idLivrable: string;
        data: LivrableUpdateRequest;
        next: () => void;
        error: () => void;
      }>(
        pipe(
          switchMap(({ idLivrable, data, next, error }) =>
            livrableService.modifier(idLivrable, data).pipe(
              map((result) => result.path.split('/')[result.path.split('/').length - 1]),
              tapResponse({
                next: (id) => {
                  store.modifierLivrable({
                    livrable: {
                      ...data,
                      id,
                    },
                  });
                  next();
                },
                error: (err: HttpErrorResponse) => {
                  console.error(err);
                  const toastrId = uuidv7();
                  toast.custom(ToastrErrorComponent, {
                    componentProps: {
                      message:
                        err.error.detail ??
                        `Un problème est survenu lors de la modification du livrable, contactez le support.`,
                      id: toastrId,
                    },
                    id: toastrId,
                  });
                  error();
                },
              }),
            ),
          ),
        ),
      ),
      supprimer: rxMethod<{ idLivrable: string; next: () => void }>(
        pipe(
          switchMap(({ idLivrable, next }) =>
            livrableService.supprimer(idLivrable).pipe(
              tapResponse({
                next: () => {
                  store.supprimerLivrable(idLivrable);
                  next();
                },
                error: (err) => {
                  console.error(err);
                  const toastrId = uuidv7();
                  toast.custom(ToastrErrorComponent, {
                    componentProps: {
                      message: `Un problème est survenu lors de la suppression du livrable, contactez le support.`,
                      id: toastrId,
                    },
                    id: toastrId,
                  });
                },
              }),
            ),
          ),
        ),
      ),
      selection: rxMethod<string>(
        pipe(
          switchMap((idLivrable) => livrableService.findById(idLivrable)),
          tapResponse({
            next: (livrable) => store.ajouterLivrable({ livrable }),
            error: (err) => {
              console.error(err);
              const toastrId = uuidv7();
              toast.custom(ToastrErrorComponent, {
                componentProps: {
                  message: `Un problème est survenu lors de la récupération du livrable, contactez le support.`,
                  id: toastrId,
                },
                id: toastrId,
              });
            },
          }),
        ),
      ),
    })),
  );
}
